import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import QRCodeStyling from "qr-code-styling";
import { jsPDF } from "jspdf";
import { registerPdfFont, PDF_FONT } from "../lib/pdf-font";
import { Download, FileImage, FileText, Copy, Check } from "lucide-react";

interface QrCodeDisplayProps {
  /** What the QR ENCODES — pass the scan URL (buildScanUrl). */
  value: string;
  /** Human-readable link shown as text + used by Copy. Defaults to `value`. */
  shareUrl?: string;
  /** QR dark-module color. */
  brandColor?: string;
  /** Couple/wedding name, printed on the PDF card. */
  title?: string;
  size?: number;
}

/** Convert a Blob to a data URL (needed for jsPDF.addImage). */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/** Trigger a browser download for a Blob. */
function downloadBlob(blob: Blob, filename: string) {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.download = filename;
  link.href = objectUrl;
  link.click();
  URL.revokeObjectURL(objectUrl);
}

export function QrCodeDisplay({
  value,
  shareUrl,
  brandColor = "#1b1817",
  title,
  size = 200,
}: QrCodeDisplayProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const qrRef = useRef<QRCodeStyling | null>(null);
  const [copied, setCopied] = useState(false);

  const displayUrl = shareUrl ?? value;

  // Construct the styled QR once and append it to the container.
  useEffect(() => {
    const qr = new QRCodeStyling({
      width: size,
      height: size,
      data: value,
      margin: 8,
      dotsOptions: { color: brandColor, type: "rounded" },
      cornersSquareOptions: { type: "extra-rounded" },
      backgroundOptions: { color: "#ffffff" },
    });
    qrRef.current = qr;
    const container = containerRef.current;
    if (container) {
      container.innerHTML = "";
      qr.append(container);
    }
    return () => {
      qrRef.current = null;
      if (container) container.innerHTML = "";
    };
    // Recreate on size change; color/data updates handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size]);

  // Update data/color when props change (without recreating the instance).
  useEffect(() => {
    qrRef.current?.update({
      data: value,
      dotsOptions: { color: brandColor, type: "rounded" },
    });
  }, [value, brandColor]);

  async function handleDownloadPng() {
    try {
      const raw = await qrRef.current?.getRawData("png");
      if (!raw) return;
      downloadBlob(raw as Blob, "qr-code.png");
    } catch (err) {
      console.error(err);
    }
  }

  async function handleDownloadSvg() {
    try {
      const raw = await qrRef.current?.getRawData("svg");
      if (!raw) return;
      downloadBlob(raw as Blob, "qr-code.svg");
    } catch (err) {
      console.error(err);
    }
  }

  async function handleDownloadPdf() {
    try {
      const raw = await qrRef.current?.getRawData("png");
      if (!raw) return;
      const dataUrl = await blobToDataUrl(raw as Blob);

      const doc = new jsPDF({ unit: "pt", format: "a4" });
      // Embedded Turkish-capable font — built-in helvetica mangles ş/ğ/ı/İ/ç/ö/ü
      // (wedding titles + Turkish CTA). Only a normal weight is embedded.
      registerPdfFont(doc);
      doc.setFont(PDF_FONT, "normal");

      const pageWidth = doc.internal.pageSize.getWidth();
      const qrSize = 260;
      const x = (pageWidth - qrSize) / 2;
      const y = 160;

      if (title) {
        doc.setFontSize(24);
        doc.setTextColor(20);
        doc.text(title, pageWidth / 2, y - 40, { align: "center" });
      }

      doc.addImage(dataUrl, "PNG", x, y, qrSize, qrSize);

      doc.setFontSize(16);
      doc.setTextColor(20);
      doc.text(t("registration.qrCtaShare"), pageWidth / 2, y + qrSize + 40, { align: "center" });

      doc.setFontSize(10);
      doc.setTextColor(120);
      doc.text(displayUrl, pageWidth / 2, y + qrSize + 64, { align: "center" });

      doc.save("qr-code.pdf");
    } catch (err) {
      console.error(err);
    }
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(displayUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <div className="flex flex-col items-center gap-3">
      <div ref={containerRef} className="rounded-lg border overflow-hidden bg-white" />
      <p className="text-xs text-muted-foreground break-all max-w-[220px] text-center">{displayUrl}</p>
      <div className="flex flex-wrap justify-center gap-2">
        <button
          onClick={handleDownloadPng}
          className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors cursor-pointer"
        >
          <Download className="h-3.5 w-3.5" />
          {t("registration.qrDownload")}
        </button>
        <button
          onClick={handleDownloadSvg}
          className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors cursor-pointer"
        >
          <FileImage className="h-3.5 w-3.5" />
          {t("registration.qrDownloadSvg")}
        </button>
        <button
          onClick={handleDownloadPdf}
          className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors cursor-pointer"
        >
          <FileText className="h-3.5 w-3.5" />
          {t("registration.qrDownloadPdf")}
        </button>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors cursor-pointer"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? t("registration.qrCopied") : t("registration.qrCopyUrl")}
        </button>
      </div>
    </div>
  );
}
